import React, { useCallback, useMemo, useRef } from "react";
import Editor from "@monaco-editor/react";

export default function CodeEditor({
                                       value,
                                       onChange,
                                       language = "python",
                                       readOnly = false,
                                       height = 280,
                                   }) {
    const editorRef = useRef(null);

    const monacoLanguage = useMemo(() => normalizeLanguage(language), [language]);

    const handleMount = useCallback((editor) => {
        editorRef.current = editor;

        editor.updateOptions({
            fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
            fontSize: 13,
            lineHeight: 20,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            smoothScrolling: true,
            wordWrap: "on",
            renderLineHighlight: "line",
            contextmenu: true,
            readOnly,
        });
    }, [readOnly]);

    return (
        <div className="rounded-lg border bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
                <div className="text-xs text-gray-600" dir="ltr">
                    <span className="font-mono">{monacoLanguage}</span>
                    {readOnly ? <span className="ml-2">(read-only)</span> : null}
                </div>
            </div>

            <div dir="ltr">
                <Editor
                    height={height}
                    language={monacoLanguage}
                    value={typeof value === "string" ? value : ""}
                    onChange={(v) => onChange(typeof v === "string" ? v : "")}
                    onMount={handleMount}
                    theme="vs-light"
                    options={{
                        readOnly,
                        lineNumbers: "on",
                        glyphMargin: false,
                        folding: true,
                        automaticLayout: true,
                        tabSize: 4,
                        insertSpaces: true,
                        formatOnPaste: false,
                        formatOnType: false,
                    }}
                />
            </div>
        </div>
    );
}

function normalizeLanguage(lang) {
    const l = String(lang || "").toLowerCase();

    if (l === "py" || l === "python") return "python";
    if (l === "js" || l === "javascript") return "javascript";
    if (l === "ts" || l === "typescript") return "typescript";
    if (l === "json") return "json";
    if (l === "html") return "html";
    if (l === "css") return "css";
    if (l === "md" || l === "markdown") return "markdown";
    if (l === "sql") return "sql";
    if (l === "c") return "c";
    if (l === "cpp" || l === "c++") return "cpp";
    if (l === "java") return "java";
    if (l === "go" || l === "golang") return "go";
    if (l === "php") return "php";
    if (l === "bash" || l === "sh" || l === "shell") return "shell";

    return l || "plaintext";
}
